import { useMemo } from "react"
import { GetApp } from "../App"
import { canonicalUnit } from "../App/Units"

export type UnitKind = "length" | "surface" | "volume" | "weight" | "power";

// The selectable unit tokens per kind. These are the values persisted into the
// ShipData `*Unit` fields and the keys (via `unit_<token>`) used for translation.
const UNIT_OPTIONS: Record<UnitKind, string[]> = {
  length: ["m", "cm", "inch", "foot", "yard"],
  surface: ["m2", "square_foot", "square_yard"],
  volume: ["l", "gal"],
  weight: ["kg", "pound", "tons"],
  power: ["kw", "hp"],
};

interface UnitSelectProps {
  kind: UnitKind;
  name: string;
  editable: boolean;
  value?: string;
  onChange?: () => void;
  omit?: string[];
  className?: string;
}

export function UnitSelect({ kind, name, editable, value, onChange, omit, className = "unit-label" }: UnitSelectProps) {
  const app = GetApp();
  const t = app.getT("ShipDataView");

  const units = useMemo(() => {
    const all = UNIT_OPTIONS[kind];
    return omit ? all.filter((unit) => !omit.includes(unit)) : all;
  }, [kind, omit]);

  // Deprecated and re-capitalized tokens still present in older persisted rows are shown
  // normalized to their canonical option; because the select is uncontrolled, an untouched select
  // then serializes the canonical value on the next save (client-side lazy migration).
  const selected = canonicalUnit(value, units) ?? value;

  return (
    <select className={className} defaultValue={selected} name={name} onChange={onChange} disabled={!editable}>
      {units.map((unit) => (
        <option value={unit} key={unit}>{t(`unit_${unit}`)}</option>
      ))}
    </select>
  );
}
